import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const VideoApproaches = ({ data }) => {
  const sectionRef = useRef(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openVideoModal = (video) => {
    setSelectedVideo(video);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeVideoModal = () => {
    setIsModalOpen(false);
    setSelectedVideo(null);
    document.body.style.overflow = 'unset';
  };

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        closeVideoModal();
      }
    };

    if (isModalOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isModalOpen]);

  useEffect(() => {
    const section = sectionRef.current;
    if (section) {
      const videoCards = section.querySelectorAll('[data-video-card]');
      
      videoCards.forEach((card, index) => {
        gsap.set(card, { opacity: 0, y: 30 });
        
        ScrollTrigger.create({
          trigger: card,
          start: 'top 85%',
          onEnter: () => {
            gsap.to(card, {
              opacity: 1,
              y: 0,
              duration: 0.6,
              ease: 'power2.out',
              delay: index * 0.1
            });
          }
        });
      });
    }

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  if (!data) return null;

  const { sectionTitle, sectionDescription, approachCategories } = data;

  return (
    <VideoApproachesSection ref={sectionRef}>
      <ContentWrapper>
        <SectionHeader>
          <SectionTitle>{sectionTitle}</SectionTitle>
          <SectionDescription>{sectionDescription}</SectionDescription>
        </SectionHeader>
        
        {approachCategories && approachCategories.length > 0 && (
          <CategoriesGrid>
            {approachCategories.map((category, index) => (
              <CategorySection key={index}>
                <CategoryTitle>{category.title}</CategoryTitle>
                {category.description && (
                  <CategoryDescription>{category.description}</CategoryDescription>
                )}
                
                {category.videos && category.videos.length > 0 && (
                  <VideosGrid videoCount={category.videos.length}>
                    {category.videos.map((video, videoIndex) => (
                      <VideoCard
                        key={videoIndex}
                        data-video-card
                        onClick={() => openVideoModal(video)}
                      >
                        <VideoThumbnail>
                          <iframe
                            src={`${video.url}?background=1&autoplay=0&loop=0&byline=0&title=0&portrait=0&muted=1`}
                            title={video.text}
                            style={{ border: 'none' }}
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowFullScreen
                          />
                          <PlayOverlay>
                            <PlayButton>
                              <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="10" fill="rgba(255,255,255,0.9)" />
                                <polygon points="10,8 16,12 10,16" fill="#000" />
                              </svg>
                            </PlayButton>
                          </PlayOverlay>
                        </VideoThumbnail>
                        <VideoTitle>{video.text}</VideoTitle>
                      </VideoCard>
                    ))}
                  </VideosGrid>
                )}
              </CategorySection>
            ))}
          </CategoriesGrid>
        )}
      </ContentWrapper>

      {/* Video Modal - Rendered in body via portal */}
      {isModalOpen && selectedVideo && createPortal(
        <VideoModal onClick={closeVideoModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={closeVideoModal} type="button">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2"/>
                <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </CloseButton>
            <ModalVideoFrame>
              <iframe
                src={selectedVideo.url.includes('?') 
                  ? `${selectedVideo.url}&autoplay=1&muted=0&loop=0&color=ffffff&title=0&byline=0&portrait=0`
                  : `${selectedVideo.url}?autoplay=1&muted=0&loop=0&color=ffffff&title=0&byline=0&portrait=0`
                }
                title={selectedVideo.text}
                style={{ border: 'none' }}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            </ModalVideoFrame>
            <ModalVideoTitle>{selectedVideo.text}</ModalVideoTitle>
          </ModalContent>
        </VideoModal>,
        document.body
      )}
    </VideoApproachesSection>
  );
};

const VideoApproachesSection = styled.section`
  padding: 4rem 0 5rem;
  position: relative;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.25rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 5rem;
`;

const SectionTitle = styled.h2`
  font-weight: 900;
  line-height: 1.1;
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  color: var(--ax-black);
  
  @media screen and (min-width: 48rem) {
    font-size: 3.5rem;
  }
`;

const SectionDescription = styled.p`
  font-size: 1.25rem;
  line-height: 1.6;
  max-width: 800px;
  margin: 0 auto;
  color: #666;
`;

const CategoriesGrid = styled.div`
  display: grid;
  gap: 5rem;
`;

const CategorySection = styled.div`
  text-align: center;
  padding: 2rem 1rem 3rem;
`;

const CategoryTitle = styled.h3`
  font-weight: 900;
  font-size: 2rem;
  margin-bottom: 2rem;
  line-height: 1.2;
  color: var(--ax-black);
  text-transform: uppercase;
  
  @media screen and (min-width: 48rem) {
    font-size: 2.25rem;
    margin-bottom: 3rem;
  }
`;

const CategoryDescription = styled.p`
  font-size: 1.125rem;
  line-height: 1.6;
  margin-bottom: 2.5rem;
  color: #666;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const VideosGrid = styled.div`
  display: grid;
  gap: 2rem;
  grid-template-columns: 1fr;
  
  @media screen and (min-width: 48rem) {
    grid-template-columns: ${props => 
      props.videoCount === 1 ? 'minmax(0, 600px)' :
      props.videoCount === 2 ? 'repeat(2, 1fr)' :
      'repeat(3, 1fr)'
    };
    justify-content: center;
  }
`;

const VideoCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  cursor: pointer;
`;

const VideoThumbnail = styled.div`
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  overflow: hidden;
  border: 0.1em solid var(--ax-gold);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  
  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    transition: transform 0.3s ease;
  }
  
  &:hover iframe {
    transform: scale(1.02);
  }
`;

const PlayOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  opacity: 0;
  transition: opacity 0.3s ease;
  
  ${VideoCard}:hover & {
    opacity: 1;
  }
`;

const PlayButton = styled.div`
  transition: transform 0.3s ease;
  
  ${VideoCard}:hover & {
    transform: scale(1.1);
  }
  
  svg {
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  }
`;


const VideoTitle = styled.h4`
  font-weight: 600;
  font-size: 1.125rem;
  margin-top: 0.75rem;
  text-align: center;
  color: var(--ax-black);
  line-height: 1.4;
`;

// Modal Styles
const VideoModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 2rem;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @media screen and (max-width: 768px) {
    padding: 1rem;
  }
`;

const ModalContent = styled.div`
  position: relative;
  width: 90vw;
  max-width: none;
  display: flex;
  flex-direction: column;
  animation: slideIn 0.3s ease;
  
  @keyframes slideIn {
    from { 
      opacity: 0; 
      transform: translateY(20px) scale(0.95); 
    }
    to { 
      opacity: 1; 
      transform: translateY(0) scale(1); 
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.7);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 10001;
  
  &:hover {
    background: rgba(0, 0, 0, 0.9);
    border-color: rgba(255, 255, 255, 0.6);
    transform: scale(1.05);
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const ModalVideoFrame = styled.div`
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  overflow: hidden;
  
  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

const ModalVideoTitle = styled.h3`
  color: white;
  font-size: 1.25rem;
  font-weight: 600;
  margin-top: 1rem;
  text-align: center;
  line-height: 1.4;
`;

export default VideoApproaches;